/**
 * Pure helpers for the Landing queue and Environments tabs: board columns, tones and labels,
 * ETAs, environment order. Everything here is tested in __tests__/model.test.ts.
 */
import { DEPLOYMENT_STATUS_LABEL, ENVIRONMENT_KIND_LABEL, LANDING_STAGES, LANDING_STATUS_LABEL, PIPELINE_RECENT_MAX, environmentView, queueEtas } from '@orgus/protocol';
import type {
  DeployTarget,
  Deployment,
  DeploymentStatus,
  EnvironmentConfig,
  EnvironmentView,
  Id,
  Landing,
  LandingEta,
  LandingProjectConfig,
  LandingQueue,
  LandingStage,
  LandingState,
  Pipeline,
  Ts,
} from '@orgus/protocol';
import type { RowStatusProps, StatusTone } from '@orgus/ui';
import { formatDuration } from '@orgus/ui';
import { deployStatus, heldDeploy } from '../project-health/model.ts';

/** Landed cards kept in the last column. */
export const LANDED_ON_BOARD = 6;
/** Failed landings stay in Needs you this long. */
export const FAILED_WINDOW_MS = 24 * 60 * 60_000;

export function landingTone(l: Pick<Landing, 'status'>): StatusTone {
  switch (l.status) {
    case 'queued':
      return 'queued';
    case 'active':
      return 'working';
    case 'parked':
      return 'waiting';
    case 'failed':
      return 'failed';
    case 'landed':
      return 'passed';
    default:
      return 'ended';
  }
}

export function landingLabel(l: Pick<Landing, 'status'>): string {
  return LANDING_STATUS_LABEL[l.status];
}

/** The plain row status for a landing (status.md): grey unless it is at work, needs you or failed. */
export function landingRowStatus(l: Pick<Landing, 'status'>): Pick<RowStatusProps, 'tone' | 'icon' | 'label'> {
  switch (l.status) {
    case 'active':
      return { tone: 'working', icon: 'working', label: LANDING_STATUS_LABEL.active };
    case 'parked':
      return { tone: 'attention', icon: 'needs-you', label: LANDING_STATUS_LABEL.parked };
    case 'failed':
      return { tone: 'danger', icon: 'failed', label: LANDING_STATUS_LABEL.failed };
    case 'landed':
      return { tone: 'success', icon: 'done', label: LANDING_STATUS_LABEL.landed };
    case 'queued':
      return { tone: 'neutral', icon: 'queued', label: LANDING_STATUS_LABEL.queued };
    default:
      return { tone: 'neutral', icon: 'stopped', label: LANDING_STATUS_LABEL[l.status] };
  }
}

/**
 * A column's count is the cards it shows, so header and column always agree; the label says
 * what they are, and for Landed how many landed in all.
 */
export function landingColumnCount(col: Pick<LandingColumn, 'stage' | 'cards'>, landedCount: number): { n: number; label: string } {
  const n = col.cards.length;
  if (col.stage === 'landed') return { n, label: n < landedCount ? `The latest ${n} of ${landedCount} landed` : `${n} landed` };
  if (col.stage === 'change') return { n, label: n === 1 ? '1 waiting its turn' : `${n} waiting their turn` };
  return { n, label: n ? `${n} at work here` : 'Nothing here' };
}

/** The line a board card carries for its column: its place, what the lander is doing, or what it became. */
export function landingCardNote(l: Pick<Landing, 'status' | 'version' | 'deploymentIds' | 'step'>, position: number, busy: boolean, machine: string): string {
  if (l.status === 'landed') return l.deploymentIds?.length ? `Landed as ${l.version}, deployed` : `Landed as ${l.version}`;
  // The hub's step may already say where ("Verifying on studio"): say the machine once.
  if (l.status === 'active') return l.step.toLowerCase().includes(` on ${machine.toLowerCase()}`) ? l.step : `${l.step} on ${machine}`;
  if (position === 1) return busy ? 'Next, waits for the current landing' : 'Next';
  return `#${position} in queue`;
}

export type QueueRunState = 'paused' | 'waiting' | 'running' | 'idle';

export function queueRunState(q: LandingQueue): QueueRunState {
  if (q.paused) return 'paused';
  if (q.activeId) return 'running';
  if (q.waiting && q.order.length) return 'waiting';
  return 'idle';
}

export const QUEUE_STATE: Record<QueueRunState, { label: string; tone: StatusTone }> = {
  paused: { label: 'Paused', tone: 'idle' },
  waiting: { label: 'Waiting for a machine', tone: 'waiting' },
  running: { label: 'Landing', tone: 'working' },
  idle: { label: 'Idle', tone: 'ended' },
};

export interface LandingCard {
  landing: Landing;
  eta: LandingEta | null;
}

export interface LandingColumn {
  stage: LandingStage;
  title: string;
  cards: LandingCard[];
  /** The active landing is in this column. */
  active: boolean;
}

/** The queue's landings as board columns: queued in queue order, the active one in its stage, the newest landed last. */
export function boardColumns(q: LandingQueue, landings: readonly Landing[], now: number): LandingColumn[] {
  const byId: Record<Id, Landing> = {};
  for (const l of landings) if (l.queueId === q.id) byId[l.id] = l;
  const etas = queueEtas(q, byId, now);
  const cols: LandingColumn[] = LANDING_STAGES.map((s) => ({ stage: s.id, title: s.title, cards: [], active: false }));
  const col = (s: LandingStage) => cols.find((c) => c.stage === s)!;
  for (const id of q.order) {
    const l = byId[id];
    if (l && l.status === 'queued') col('change').cards.push({ landing: l, eta: etas[id] ?? null });
  }
  const active = q.activeId ? byId[q.activeId] : undefined;
  if (active && active.status === 'active') {
    const c = col(active.stage === 'change' ? 'verify' : active.stage);
    c.cards.push({ landing: active, eta: etas[active.id] ?? null });
    c.active = true;
  }
  const landed = Object.values(byId)
    .filter((l) => l.status === 'landed')
    .sort((a, b) => (b.endedAt ?? 0) - (a.endedAt ?? 0))
    .slice(0, LANDED_ON_BOARD);
  col('landed').cards.push(...landed.map((l) => ({ landing: l, eta: null })));
  return cols;
}

/** Parked landings, and failed ones from the last day: what needs a person, newest first. */
export function needsAttention(landings: readonly Landing[], now: number, queueId?: string): Landing[] {
  return landings
    .filter((l) => (queueId == null || l.queueId === queueId) && (l.status === 'parked' || (l.status === 'failed' && (l.endedAt ?? 0) >= now - FAILED_WINDOW_MS)))
    .sort((a, b) => Number(b.status === 'parked') - Number(a.status === 'parked') || (b.endedAt ?? 0) - (a.endedAt ?? 0));
}

/** "Lands in about 4 min", "ETA after the first landing", "Paused". */
export function etaText(eta: LandingEta | null, now: number, paused: boolean): string {
  if (paused) return 'Queue paused';
  if (!eta || eta.etaAt == null) return 'No ETA until a first landing';
  const ms = eta.etaAt - now;
  return ms <= 30_000 ? 'Lands any moment' : `Lands in about ${formatDuration(Math.round(ms / 60_000) * 60_000 || ms)}`;
}

/** Move one id up (-1) or down (+1) in an order: keyboard reordering. */
export function moveInOrder(order: readonly Id[], id: Id, delta: number): Id[] {
  const i = order.indexOf(id);
  const j = i + delta;
  if (i < 0 || j < 0 || j >= order.length) return [...order];
  const next = [...order];
  next.splice(i, 1);
  next.splice(j, 0, id);
  return next;
}

export function sameOrder(a: readonly Id[], b: readonly Id[]): boolean {
  return a.length === b.length && a.every((x, i) => x === b[i]);
}

/** Queues sorted for the switcher: busiest first, then by name. */
export function sortQueues(queues: readonly LandingQueue[], projectName: (id: Id) => string): LandingQueue[] {
  return [...queues].sort(
    (a, b) =>
      Number(!!b.activeId) - Number(!!a.activeId) ||
      b.order.length - a.order.length ||
      projectName(a.projectId).localeCompare(projectName(b.projectId)) ||
      a.target.localeCompare(b.target),
  );
}

// ---------------------------------------------------------------------------
// Environments
// ---------------------------------------------------------------------------

const KIND_RANK = { preview: 0, staging: 1, production: 2 } as const;

export function sortEnvironments(envs: readonly EnvironmentConfig[]): EnvironmentConfig[] {
  return [...envs].sort((a, b) => KIND_RANK[a.kind] - KIND_RANK[b.kind] || a.name.localeCompare(b.name));
}

export function envKindLabel(e: Pick<EnvironmentConfig, 'kind'>): string {
  return ENVIRONMENT_KIND_LABEL[e.kind];
}

/**
 * The environment a promote into `env` takes its version from: the nearest earlier one with
 * something live, preferring staging over preview. Environments that follow a branch get their
 * versions from landings, so they have no promote source.
 */
export function promoteSource(views: readonly EnvironmentView[], env: string): EnvironmentView | null {
  const i = views.findIndex((v) => v.config.name === env);
  if (i < 0 || views[i]!.config.autoDeployFrom) return null;
  const earlier = views.slice(0, i).filter((v) => v.current && v.config.kind !== views[i]!.config.kind);
  return [...earlier].reverse().find((v) => v.config.kind !== 'preview') ?? earlier.at(-1) ?? null;
}

/**
 * The projects the Environments tab shows: the one picked, else every project that uses the
 * landing queue or reports its own environments (deploys.json), by name. The Overview's flow
 * draws all of them, so the tab must not hide any behind its picker.
 */
export function environmentProjects<P extends { id: Id; name: string }>(projects: Readonly<Record<Id, P>>, listed: (id: Id) => boolean, scope: Id | null): P[] {
  if (scope) return projects[scope] ? [projects[scope]] : [];
  return Object.values(projects)
    .filter((p) => listed(p.id))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function projectEnvironments(s: LandingState, config: LandingProjectConfig, now: number): EnvironmentView[] {
  return sortEnvironments(config.environments).map((e) => environmentView(config.projectId, e, s.deployments, now));
}

export function deploymentTone(d: Pick<Deployment, 'status'>): StatusTone {
  const map: Record<DeploymentStatus, StatusTone> = {
    deploying: 'working',
    live: 'passed',
    failed: 'failed',
    superseded: 'ended',
    'rolled-back': 'ended',
    held: 'waiting',
  };
  return map[d.status];
}

/** The plain status of a deployment for rows and cards: live is the one success worth showing. */
export function deploymentRowStatus(d: Pick<Deployment, 'status'>): Pick<RowStatusProps, 'tone' | 'icon' | 'label'> {
  const label = DEPLOYMENT_STATUS_LABEL[d.status];
  switch (d.status) {
    case 'deploying':
      return { tone: 'working', icon: 'working', label };
    case 'live':
      return { tone: 'success', icon: 'done', label };
    case 'failed':
      return { tone: 'danger', icon: 'failed', label };
    case 'held':
      return { tone: 'attention', icon: 'paused', label };
    default:
      return { tone: 'neutral', icon: 'ended', label };
  }
}

/** Versions that can be deployed: every landed landing of the project, newest first, once each. */
export function deployableVersions(s: LandingState, projectId: Id): { version: string; sha: string; title: string; at: number; landingId: Id | null }[] {
  const out = new Map<string, { version: string; sha: string; title: string; at: number; landingId: Id | null }>();
  for (const l of s.landings) {
    if (l.projectId !== projectId || !l.landedSha || !l.version) continue;
    out.set(l.version, { version: l.version, sha: l.landedSha, title: l.title, at: l.endedAt ?? l.updatedAt, landingId: l.id });
  }
  for (const d of s.deployments) {
    if (d.projectId !== projectId || out.has(d.version)) continue;
    out.set(d.version, { version: d.version, sha: d.sha, title: s.landings.find((l) => l.id === d.landingId)?.title ?? 'Earlier deployment', at: d.startedAt, landingId: d.landingId });
  }
  return [...out.values()].sort((a, b) => b.at - a.at);
}

/** The config the page should use for a project: the saved one or the hub's defaults (sent in state). */
export function configFor(s: LandingState, projectId: Id): LandingProjectConfig | undefined {
  return s.configs.find((c) => c.projectId === projectId);
}

/** What an empty landing log says: still waiting for output while the run goes on, plainly none once it ended. */
export function landingLogEmptyText(running: boolean): string {
  return running ? 'No output yet.' : 'No output.';
}

/**
 * Landings a pipeline finished since local midnight. Its recent list holds only the newest
 * PIPELINE_RECENT_MAX, so when all of them are today the count is a floor (`atLeast`).
 */
export function landedToday(p: Pick<Pipeline, 'recent'>, now: Ts): { count: number; atLeast: boolean } {
  const midnight = new Date(now);
  midnight.setHours(0, 0, 0, 0);
  const today = p.recent.filter((i) => (i.endedAt ?? -1) >= midnight.getTime());
  return { count: today.filter((i) => i.state === 'passed').length, atLeast: p.recent.length >= PIPELINE_RECENT_MAX && today.length === p.recent.length };
}

/** A reported target's known phases (follow-main's states for Local main): serving is the quiet default. */
const PHASE_STATUS: Record<string, Pick<RowStatusProps, 'tone' | 'icon'>> = {
  serving: { tone: 'neutral', icon: 'done' },
  starting: { tone: 'working', icon: 'working' },
  installing: { tone: 'working', icon: 'working' },
  building: { tone: 'working', icon: 'working' },
  restarting: { tone: 'working', icon: 'working' },
  draining: { tone: 'neutral', icon: 'queued' },
  failed: { tone: 'danger', icon: 'failed' },
  stopped: { tone: 'neutral', icon: 'stopped' },
};

/** A deploy target a project reports (Project Protocol deploys.json): its own phase in words, else what its state says. */
export function reportedTargetStatus(t: Pick<DeployTarget, 'deploying' | 'behind' | 'live' | 'last' | 'phase'>): Pick<RowStatusProps, 'tone' | 'icon' | 'label'> {
  const base = deployStatus(t);
  // A refused or cancelled deploy outranks the phase ("live"): what is live is not what was asked for.
  if (!t.phase || heldDeploy(t)) return base;
  const label = `${t.phase.charAt(0).toUpperCase()}${t.phase.slice(1)}`;
  return { ...(Object.hasOwn(PHASE_STATUS, t.phase) ? PHASE_STATUS[t.phase] : base), label };
}
